import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ClipboardCopy,
  Eye,
  EyeOff,
  Loader2,
  QrCode,
  RotateCcw,
  Shield,
} from 'lucide-react';
import {
  disableTwoFactor,
  getTwoFactorStatus,
  startTwoFactorSetup,
  verifyAndEnableTwoFactor,
} from '../services/twoFactorApi';
import type { TwoFactorSetupResponse, TwoFactorStatus } from '../services/twoFactorApi';
import { useToast } from './Toast';
import { useI18n } from '../contexts/I18nContext';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  padding: '20px',
  marginBottom: '20px',
  border: '1px solid #e5e7eb',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
};

const actionButtonStyle = (variant: 'primary' | 'secondary' | 'danger'): React.CSSProperties => {
  const colorMap = {
    primary: { bg: '#3b82f6', hover: '#2563eb' },
    secondary: { bg: '#f3f4f6', hover: '#e5e7eb' },
    danger: { bg: '#ef4444', hover: '#dc2626' },
  }[variant];

  return {
    padding: '10px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: colorMap.bg,
    color: variant === 'secondary' ? '#111827' : '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    transition: 'background-color 0.2s ease',
  };
};

interface Props {
  compact?: boolean;
}

const TwoFactorSettings: React.FC<Props> = ({ compact = false }) => {
  const { t } = useI18n();
  const { showError, showSuccess } = useToast();

  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const hasActiveSetup = !!setupData;

  const loadStatus = async () => {
    setIsLoading(true);
    try {
      const data = await getTwoFactorStatus();
      setStatus(data);
    } catch (error: any) {
      console.error('Erro ao carregar status 2FA', error);
      showError('Não foi possível carregar o status do 2FA.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleStartSetup = async () => {
    setIsSubmitting(true);
    setBackupCodes(null);
    try {
      const response = await startTwoFactorSetup();
      setSetupData(response);
      setVerificationCode('');
      showSuccess('Código gerado! Escaneie o QR Code para continuar.');
    } catch (error: any) {
      console.error('Erro ao iniciar setup 2FA', error);
      showError(error?.response?.data?.error || 'Falha ao iniciar configuração 2FA.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!setupData) return;
    if (verificationCode.trim().length < 6) {
      showError('Informe um código de 6 dígitos.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await verifyAndEnableTwoFactor(setupData.secret, verificationCode.trim());
      setBackupCodes(response.backup_codes || []);
      setSetupData(null);
      setVerificationCode('');
      await loadStatus();
      showSuccess('2FA habilitado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao verificar 2FA', error);
      showError(error?.response?.data?.error || 'Código inválido, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!disableCode.trim()) {
      showError('Informe o código para desativar.');
      return;
    }
    setIsSubmitting(true);
    try {
      await disableTwoFactor(disableCode.trim());
      setDisableCode('');
      await loadStatus();
      showSuccess('2FA desativado com sucesso.');
    } catch (error: any) {
      console.error('Erro ao desativar 2FA', error);
      showError(error?.response?.data?.error || 'Não foi possível desativar o 2FA.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopySecret = async () => {
    if (!setupData) return;
    await navigator.clipboard.writeText(setupData.secret);
    showSuccess('Chave secreta copiada para a área de transferência.');
  };

  const handleCopyBackupCodes = async () => {
    if (!backupCodes || backupCodes.length === 0) return;
    await navigator.clipboard.writeText(backupCodes.join('\n'));
    showSuccess('Códigos de backup copiados.');
  };

  const handleDownloadBackupCodes = () => {
    if (!backupCodes || backupCodes.length === 0) return;
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'house-keep-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const statusBadge = useMemo(() => {
    if (!status) return null;
    const baseStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
    };

    if (status.enabled) {
      return (
        <span style={{ ...baseStyle, backgroundColor: '#dcfce7', color: '#166534' }}>
          <CheckCircle size={14} />{t('twoFactor.enabled')}</span>
      );
    }
    return (
      <span style={{ ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' }}>
        <AlertTriangle size={14} />{t('twoFactor.disabled')}</span>
    );
  }, [status]);

  return (
    <section style={cardStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
            {t('twoFactor.title')}
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
            Proteja seu acesso exigindo um código adicional gerado por um aplicativo autenticador.
          </p>
        </div>
        {statusBadge}
      </header>

      {status?.required && (
        <div
          style={{
            marginTop: '16px',
            padding: '10px 12px',
            borderRadius: '6px',
            backgroundColor: '#fff7ed',
            border: '1px solid #fed7aa',
            fontSize: '13px',
            color: '#9a3412',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Shield size={16} />{t('twoFactor.adminMustKeep')}</div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
          <Loader2 className="spin" size={28} color="#6b7280" />
        </div>
      ) : (
        <>
          {!status?.enabled && (
            <div style={{ marginTop: '20px' }}>
              <div
                style={{
                  padding: '16px',
                  border: '1px solid #dbeafe',
                  backgroundColor: '#eff6ff',
                  borderRadius: '8px',
                  marginBottom: '20px',
                }}
              >
                <p style={{ margin: 0, color: '#1e3a8a', fontSize: '14px' }}>{t('twoFactor.disabledInfo')}</p>
              </div>

              <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '16px' }}>
                Para ativar, escaneie o QR Code em um aplicativo como Google Authenticator, Authy,
                Microsoft Authenticator ou 1Password. Depois, informe o código gerado para concluir.
              </p>

              {!hasActiveSetup ? (
                <button
                  style={actionButtonStyle('primary')}
                  disabled={isSubmitting}
                  onClick={handleStartSetup}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Shield size={16} />{t('twoFactor.activate')}</>
                  )}
                </button>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gap: '20px',
                    gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
                    marginTop: '20px',
                  }}
                >
                  <div
                    style={{
                      border: '1px dashed #d1d5db',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                    }}
                  >
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                      Escaneie este QR Code
                    </p>
                    <div
                      style={{
                        display: 'inline-flex',
                        padding: '12px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      {setupData?.qr_code_url ? (
                        <img
                          src={setupData.qr_code_url}
                          alt="QR Code 2FA"
                          style={{ width: '180px', height: '180px' }}
                        />
                      ) : (
                        <QrCode size={160} color="#9ca3af" />
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
                      Use qualquer app compatível com TOTP.
                    </p>
                  </div>

                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>
                        Chave secreta
                        <span
                          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => setShowSecret(!showSecret)}
                        >
                          {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                        </span>
                      </label>
                      <div
                        style={{
                          ...inputStyle,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: '#f9fafb',
                        }}
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                          {showSecret ? setupData?.secret : '••••••••••••••••'}
                        </span>
                        <button
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: '#2563eb',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                          onClick={handleCopySecret}
                          type="button"
                        >
                          <ClipboardCopy size={16} />{t('common.copy')}</button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="2fa-verification-code" style={labelStyle}>Código do aplicativo</label>
                      <input
                        id="2fa-verification-code"
                        name="2fa-verification-code"
                        type="text"
                        inputMode="numeric"
                        style={inputStyle}
                        placeholder="Ex: 123456"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </div>

                    <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        style={actionButtonStyle('primary')}
                        onClick={handleVerifyCode}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="spin" size={16} />
                            Validando...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} />
                            Verificar e ativar
                          </>
                        )}
                      </button>
                      <button
                        style={actionButtonStyle('secondary')}
                        onClick={() => {
                          setSetupData(null);
                          setVerificationCode('');
                        }}
                      >
                        <RotateCcw size={16} />{t('actions.cancel')}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {status?.enabled && (
            <div style={{ marginTop: '20px', display: 'grid', gap: '20px' }}>
              <div
                style={{
                  padding: '16px',
                  border: '1px solid #dbeafe',
                  backgroundColor: '#eff6ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', color: '#1d4ed8' }}>{t('twoFactor.enabled')}</h4>
                    <p style={{ margin: 0, color: '#1e3a8a', fontSize: '14px' }}>{t('twoFactor.enabledInfo')}</p>
                  </div>
                  <CheckCircle size={24} color="#166534" />
                </div>
              </div>

              {backupCodes && backupCodes.length > 0 && (
                <div
                  style={{
                    border: '1px solid #fde68a',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fffbeb',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, color: '#92400e' }}>Códigos de backup (mostrar uma vez)</h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={actionButtonStyle('secondary')}
                        onClick={handleCopyBackupCodes}
                      >
                        <ClipboardCopy size={16} />{t('common.copy')}</button>
                      <button
                        style={actionButtonStyle('secondary')}
                        onClick={handleDownloadBackupCodes}
                      >
                        <RotateCcw size={16} />{t('common.download')}</button>
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '12px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '8px',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  >
                    {backupCodes.map((code) => (
                      <div
                        key={code}
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          border: '1px dashed #f59e0b',
                          textAlign: 'center',
                          backgroundColor: '#fff7ed',
                        }}
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: '10px', color: '#b45309', fontSize: '12px' }}>
                    Estes códigos só aparecem agora. Guarde em local seguro.
                  </p>
                </div>
              )}

              <div
                style={{
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                }}
              >
                <h4 style={{ margin: '0 0 12px 0', color: '#991b1b' }}>{t('twoFactor.deactivateTitle')}</h4>
                {status.required && (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '10px',
                      borderRadius: '6px',
                      backgroundColor: '#fff7ed',
                      border: '1px solid #fed7aa',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '13px', color: '#9a3412' }}>
                      <strong>{t('twoFactor.warning')}</strong>{t('twoFactor.deactivateWarning')}</p>
                  </div>
                )}
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#7f1d1d' }}>
                  Para desativar, informe um código do autenticador ou um código de backup para confirmar.
                </p>
                <input
                  id="2fa-disable-code"
                  name="2fa-disable-code"
                  type="text"
                  inputMode="numeric"
                  style={inputStyle}
                  placeholder="Código atual ou de backup"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  maxLength={8}
                  autoComplete="one-time-code"
                  disabled={status.required}
                />
                <button
                  style={{ ...actionButtonStyle('danger'), marginTop: '12px' }}
                  onClick={handleDisableTwoFactor}
                  disabled={isSubmitting || status.required}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Removendo...
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={16} />{t('twoFactor.deactivateTitle')}</>
                  )}
                </button>
                {status.required && (
                  <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#b45309' }}>{t('twoFactor.requiredDisabled')}</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default TwoFactorSettings;

